<template>
  <DrawerVue :refreshDisplay="refreshDisplay" class="shopping-card-drawer">
    <v-container>
      <v-row>
        <v-col>
          <h2>{{ $t("basics.shoppingCart") }}</h2>
        </v-col>
      </v-row>

      <v-alert v-if="statusError" type="warning" variant="tonal" class="mb-4" role="alert">
        {{ $t("messages.shoppingCartWarningNoConnection") }}
        <v-btn variant="text" :loading="checkingStatus" @click="checkCartStatus">{{ $t('basics.checkAgain') }}</v-btn>
      </v-alert>
      <v-alert v-if="hasBlockedItems" type="warning" variant="tonal" class="mb-4" role="alert">
        {{ $t("messages.shoppingCartWarningAlreadyBought") }}
      </v-alert>
      <v-alert v-if="bundleOverlapItems.length" type="warning" variant="tonal" class="mb-4" role="alert">
        {{ $t("messages.shoppingCartWarningBundleOverlap", { names: bundleOverlapItems.map(item => item.name).join(", ") }) }}
      </v-alert>
      <template v-if="oneTimeItems.length">
        <v-row>
          <v-col>
            <h3>{{ $t("basics.oneTimePaymentsAndAppointments") }}</h3>
          </v-col>
        </v-row>

        <v-row v-for="entry in oneTimeItems" :key="`${getItemType(entry.item)}-${entry.item.id}-${entry.index}`"
          :class="{ 'cart-item-blocked': isBlocked(entry.item) }">
          <v-col cols="7">
            <h3>{{ getItemProduct(entry.item).name }}</h3>
            <p>{{ shortDescription(entry.item) }}</p>
            <v-chip v-if="getItemType(entry.item) === 'digitalbundle'" size="small" color="primary" class="mt-2">
              {{ $t('basics.digitalBundle') }}
            </v-chip>
            <p v-if="isBlocked(entry.item)" class="cart-item-warning" role="status">
              <v-icon size="small">mdi-alert-circle-outline</v-icon>
              {{ itemStatus(entry.item).message }}
            </p>

            <v-chip v-if="getItemType(entry.item) === 'appointment'" size="small" color="primary" class="mt-2">
              <v-icon start>
                {{
                  entry.item.num_patients === 1
                    ? "mdi-account"
                    : "mdi-account-multiple"
                }}
              </v-icon>
              {{
                $t(
                  entry.item.num_patients === 1
                    ? "basics.soloAppointment"
                    : "basics.groupAppointment"
                )
              }}
            </v-chip>
          </v-col>

          <v-col cols="4" class="d-flex align-end justify-end">
            <p>1x {{ getItemProduct(entry.item).price }}€</p>
          </v-col>

          <v-col cols="1" class="d-flex align-end justify-end">
            <v-icon :disabled="loading" @click="deleteItem(entry.index)" style="cursor: pointer">
              mdi-delete
            </v-icon>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn block large class="primaryButton" :disabled="loading || checkingStatus || statusError || !buyableOneTimeItems.length" :loading="activeCheckout === 'oneTime'"
              @click="goToCheckout(buyableOneTimeItems.map(entry => entry.item), 'oneTime')">
              {{ $t("messages.buy") }}
            </v-btn>
          </v-col>
        </v-row>
      </template>

      <v-row v-if="shoppingCartItems.length === 0">
        <v-col>
          {{ $t("messages.noItems") }}
        </v-col>
      </v-row>
    </v-container>
  </DrawerVue>
</template>

<script>
import DrawerVue from "../main/Drawer.vue";

export default {
  name: "shoppingCartDrawer",

  props: ["refreshDisplay"],

  components: {
    DrawerVue,
  },

  data() {
    return {
      loading: false,
      activeCheckout: null,
      cartStatus: {},
      checkingStatus: false,
      statusError: false,
      statusRequest: 0,
    };
  },

  computed: {
    shoppingCartItems() {
      return (this.$store.state.shopping_cart_items || []).filter(item => !this.isSubscription(item));
    },

    oneTimeItems() {
      return this.shoppingCartItems
        .map((item, index) => ({ item, index }))
        .filter(entry => !this.isSubscription(entry.item));
    },

    buyableOneTimeItems() {
      return this.oneTimeItems.filter(entry => !this.isBlocked(entry.item));
    },

    hasBlockedItems() {
      return this.shoppingCartItems.some(item => this.isBlocked(item));
    },

    // Einzelmedien, die zugleich in einem Bundle im Warenkorb stecken, würden doppelt berechnet.
    bundleOverlapItems() {
      const bundledMediaIds = new Set(this.shoppingCartItems
        .filter(item => this.getItemType(item) === "digitalbundle")
        .flatMap(item => (item.digital_medias || []).map(media =>
          media && typeof media === "object" ? media.id : media)));
      return this.shoppingCartItems.filter(item =>
        this.getItemType(item) === "digitalmedia" && bundledMediaIds.has(item.id));
    },

    username() {
      return this.$store.state.name;
    },
  },

  watch: {
    refreshDisplay(value) {
      if (value) this.refreshCartStatus();
    },
    shoppingCartItems: {
      deep: true,
      handler() { this.checkCartStatus(); },
    },
    username() { this.refreshCartStatus(); },
  },

  mounted() {
    // Entfernt Abos aus Warenkörben, die vor der Umstellung gespeichert wurden.
    const storedItems = this.$store.state.shopping_cart_items || [];
    if (storedItems.some(item => this.isSubscription(item))) {
      this.$store.dispatch("setShoppingCartItems", this.shoppingCartItems);
    }
    this.refreshCartStatus();
  },

  methods: {
    async claimFreeItem(item) {
      const type = this.getItemType(item);
      if (this.loading || !["digitalmedia", "digitalbundle"].includes(type)) return false;
      if (!this.username) {
        localStorage.setItem("in_checkout_process", JSON.stringify({
          free_claim: true, items: [{ item_id: item.id, item_type: type }],
        }));
        this.$router.push("/login");
        return false;
      }
      this.loading = true;
      this.activeCheckout = `free-${type}-${item.id}`;
      try {
        await this.axios.post(this.axios.defaults.baseURL + "media/payment/free/", {
          item_id: item.id, item_type: type,
        });
        const cart = (this.$store.state.shopping_cart_items || []).filter(entry =>
          !(entry.id === item.id && this.getItemType(entry) === type));
        this.$store.dispatch("setShoppingCartItems", cart);
        localStorage.removeItem("in_checkout_process");
        window.dispatchEvent(new CustomEvent("digital-content-added"));
        return true;
      } catch (error) {
        window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: error }));
        return false;
      } finally {
        this.loading = false;
        this.activeCheckout = null;
      }
    },
    statusKey(item) {
      return `${this.getItemType(item)}-${item.id}`;
    },
    itemStatus(item) {
      return this.cartStatus[this.statusKey(item)] || {};
    },
    isBlocked(item) {
      return this.itemStatus(item).blocked === true;
    },
    async refreshCartStatus() {
      if (await this.checkCartStatus()) this.detectActiveCheckout();
    },
    async checkCartStatus(itemsToCheck = this.shoppingCartItems) {
      // Vue übergibt bei @click das Event statt einer Artikelliste.
      if (!Array.isArray(itemsToCheck)) itemsToCheck = this.shoppingCartItems;
      const requestId = ++this.statusRequest;
      this.statusError = false;
      if (!this.username || !itemsToCheck.length) {
        this.cartStatus = {};
        this.checkingStatus = false;
        return true;
      }
      this.checkingStatus = true;
      try {
        // The server rejects duplicate positions; check each reference once.
        const references = new Map(itemsToCheck.map(item => [
          this.statusKey(item), { item_id: item.id, item_type: this.getItemType(item) },
        ]));
        const results = [];
        const items = [...references.values()];
        for (let start = 0; start < items.length; start += 100) {
          const response = await this.axios.post(
            this.axios.defaults.baseURL + "media/payment/cart-status/",
            { line_items: items.slice(start, start + 100) }
          );
          if (!Array.isArray(response.data.items)) throw new Error(this.$t("messages.invalidPurchaseStatus"));
          results.push(...response.data.items);
        }
        if (requestId !== this.statusRequest) return false;
        const statusByKey = new Map(results.map(item => [`${item.item_type}-${item.item_id}`, item]));
        if (items.some(item => typeof statusByKey.get(`${item.item_type}-${item.item_id}`)?.blocked !== "boolean")) {
          throw new Error(this.$t("messages.incompletePurchaseStatus"));
        }
        this.cartStatus = Object.fromEntries(results.map(item => [
          `${item.item_type}-${item.item_id}`, item,
        ]));
        return true;
      } catch (error) {
        if (requestId === this.statusRequest) this.statusError = true;
        return false;
      } finally {
        if (requestId === this.statusRequest) this.checkingStatus = false;
      }
    },
    getItemType(item) {
      return item.item_type || (item.product ? "appointment" : "digitalmedia");
    },

    getItemProduct(item) {
      return this.getItemType(item) === "appointment"
        ? item.product || item
        : item;
    },

    isSubscription(item) {
      return (
        ["digitalmedia", "digitalbundle"].includes(this.getItemType(item)) &&
        (item.is_subscription === true || (this.getItemType(item) === "digitalbundle" && item.payment_type === 1))
      );
    },

    subscriptionKey(item) {
      return `subscription-${this.getItemType(item)}-${item.id}`;
    },

    shortDescription(item) {
      const description = this.getItemProduct(item).description || "";
      return description.length > 30
        ? description.substring(0, 28) + "..."
        : description;
    },

    async goToCheckout(items, checkoutKey) {
      if (this.loading || this.checkingStatus || !items.length) {
        return;
      }

      const subscriptions = items.filter(item => this.isSubscription(item));
      if (subscriptions.length && (subscriptions.length !== 1 || items.length !== 1)) return;

      if (!this.username) {
        // Nur die ausgewählte Checkout-Gruppe nach dem Login fortsetzen.
        localStorage.setItem(
          "in_checkout_process",
          JSON.stringify({
            checkoutKey,
            items: items.map(item => ({
              item_id: item.id,
              item_type: this.getItemType(item),
              is_subscription: this.isSubscription(item),
            })),
          })
        );

        this.$router.push("/login");
        return;
      }

      if (!(await this.checkCartStatus(items))) return;
      const availableItems = items.filter(item => !this.isBlocked(item));
      // Show the warning before proceeding if the selected group has changed.
      if (availableItems.length !== items.length) return;

      this.loading = true;
      this.activeCheckout = checkoutKey;

      const frontendURL = this.$localURL.replace(/\/+$/, "");

      const formData = {
        line_items: items.map(item => ({
          item_id: item.id,
          item_type: this.getItemType(item),
          quantity: 1,
        })),
        success_url: `${frontendURL}/main/purchases/`,
        cancel_url: `${frontendURL}/shop/`,
      };

      try {
        const response = await this.axios.post(
          this.axios.defaults.baseURL + "media/payment/create/",
          formData
        );

        if (response.data.checkout_url) {
          localStorage.removeItem("in_checkout_process");
          window.location.assign(response.data.checkout_url);
        }
      } catch (error) {
        await this.checkCartStatus(items);
        window.dispatchEvent(
          new CustomEvent("showErrorDisplay", { detail: error })
        );
      } finally {
        this.loading = false;
        this.activeCheckout = null;
      }
    },

    deleteItem(index) {
      if (this.loading) {
        return;
      }

      const items = [...this.shoppingCartItems];
      items.splice(index, 1);
      this.$store.dispatch("setShoppingCartItems", items);
    },

    detectActiveCheckout() {
      if (!this.username || this.loading) {
        return;
      }

      const stored = localStorage.getItem("in_checkout_process");
      if (!stored) {
        return;
      }

      let pending;

      try {
        pending = JSON.parse(stored);
      } catch {
        localStorage.removeItem("in_checkout_process");
        return;
      }

      // Alte Boolean-Markierung nicht automatisch als Gesamtcheckout starten.
      if (
        !pending ||
        typeof pending !== "object" ||
        !Array.isArray(pending.items) ||
        !pending.items.length
      ) {
        localStorage.removeItem("in_checkout_process");
        return;
      }

      if (pending.free_claim === true) {
        const reference = pending.items[0];
        localStorage.removeItem("in_checkout_process");
        if (pending.items.length === 1 && ["digitalmedia", "digitalbundle"].includes(reference.item_type) &&
          Number.isInteger(reference.item_id) && reference.item_id > 0) {
          this.claimFreeItem({ id: reference.item_id, item_type: reference.item_type });
        }
        return;
      }

      const items = pending.items.map(reference =>
        ["digitalmedia", "digitalbundle"].includes(reference.item_type) && reference.is_subscription === true
          ? { id: reference.item_id, item_type: reference.item_type, is_subscription: true }
          : this.shoppingCartItems.find(
          item =>
            item.id === reference.item_id &&
            this.getItemType(item) === reference.item_type
        )
      );

      // Bei verändertem Warenkorb die Auswahl erneut bestätigen lassen.
      if (items.some(item => !item)) {
        localStorage.removeItem("in_checkout_process");
        return;
      }

      const subscriptions = items.filter(item => this.isSubscription(item));

      if (
        subscriptions.length &&
        (subscriptions.length !== 1 || items.length !== 1)
      ) {
        localStorage.removeItem("in_checkout_process");
        return;
      }

      localStorage.removeItem("in_checkout_process");

      this.goToCheckout(
        items,
        subscriptions.length ? this.subscriptionKey(items[0]) : "oneTime"
      );
    },
  },
};
</script>

<style scoped>
.cart-item-blocked {
  background-color: #f2f2f2 !important;
  color: #616161;
  border-radius: 6px;
}
.cart-item-warning {
  margin-top: 8px;
  color: #795000;
  font-size: 0.875rem;
}
</style>
